import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  alpha,
  Box,
  Breadcrumbs,
  Chip,
  CircularProgress,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
  type Theme,
} from '@mui/material'
import {
  CheckSquare,
  ChevronRight,
  Home,
  MinusSquare,
  Search,
  ShieldCheck,
  Square,
} from 'lucide-react'
import { BorgApiClient, type Repository } from '../services/borgApi/client'
import type { Archive } from '../types'
import { translateBackendKey } from '../utils/translateBackendKey'
import type { RestorePathMetadata } from '../utils/restorePaths'
import FileTypeIcon from './FileTypeIcon'

export interface ArchiveItem {
  name: string
  type: 'file' | 'directory'
  path: string
  size?: number
  managed?: boolean
  managed_type?: string
}

const RESTORE_CANARY_MANAGED_TYPE = 'restore_canary'

// Give up polling a stuck agent listing after this long and surface a timeout,
// rather than hammering the server every 1.5s forever while the dialog stays open.
const BROWSE_POLL_TIMEOUT_MS = 10 * 60 * 1000

function isRestoreCanaryPath(path?: string) {
  const normalized = (path || '').replace(/^\/+/, '').replace(/\/+$/, '')
  return normalized === '.borg-ui' || normalized.startsWith('.borg-ui/')
}

function isRestoreCanaryItem(item: Pick<ArchiveItem, 'path' | 'managed_type'>) {
  return item.managed_type === RESTORE_CANARY_MANAGED_TYPE || isRestoreCanaryPath(item.path)
}

export interface ArchivePathSelectionData {
  selectedPaths: string[]
  selectedItems?: RestorePathMetadata[]
}

export interface ArchiveBrowseState {
  currentPath: string
  /** The rows actually on screen, filter applied, so a wrapper's keyboard
   *  cursor and the list agree on what index means. */
  items: ArchiveItem[]
  navigateTo: (path: string) => void
  activateItem: (item: ArchiveItem) => void
  focusFilter: () => void
}

interface ArchivePathSelectorProps {
  repository: Repository
  archive: Pick<Archive, 'id' | 'name'>
  data: ArchivePathSelectionData
  onChange: (data: Partial<ArchivePathSelectionData>) => void
  title?: string
  subtitle?: string
  helpText?: string
  /** `embedded` drops the heading, selection bar, and helper caption so a
   *  page that owns those surfaces (the archive Files tab) shows them once. */
  variant?: 'standalone' | 'embedded'
  /** Optional escape hatch reporting the current browse state and imperative
   *  navigation, so a wrapper (e.g. keyboard shortcuts) can drive this
   *  component without it becoming a controlled component. */
  onBrowseStateChange?: (state: ArchiveBrowseState) => void
  /** Row the wrapper's keyboard cursor sits on, so arrow keys have something
   *  visible to move. Undefined leaves the list unhighlighted. */
  activeIndex?: number
}

export default function ArchivePathSelector({
  repository,
  archive,
  data,
  onChange,
  title,
  subtitle,
  helpText,
  variant = 'standalone',
  onBrowseStateChange,
  activeIndex,
}: ArchivePathSelectorProps) {
  const embedded = variant === 'embedded'
  const { t } = useTranslation()
  const [filter, setFilter] = useState('')
  const filterRef = useRef<HTMLInputElement>(null)
  const [currentPath, setCurrentPath] = useState<string>('')
  const [items, setItems] = useState<ArchiveItem[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [awaitingAgent, setAwaitingAgent] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const selectedPaths = new Set(data.selectedPaths || [])
  const selectedItems = data.selectedItems || []

  useEffect(() => {
    let cancelled = false

    const fetchContents = async () => {
      setLoading(true)
      setError(null)
      setAwaitingAgent(false)

      try {
        const client = new BorgApiClient(repository)
        // Agent listings are asynchronous: a 202 means the job is still running,
        // so poll with the returned id until it completes — but give up after
        // BROWSE_POLL_TIMEOUT_MS so a wedged job doesn't poll the server forever.
        const deadline = Date.now() + BROWSE_POLL_TIMEOUT_MS
        let response = await client.getArchiveContents(archive.id, archive.name, currentPath)
        let jobId: number | undefined
        while (response.status === 202) {
          if (cancelled) return
          if (Date.now() > deadline) {
            setError(t('wizard.restoreFiles.loadContentsTimeout'))
            return
          }
          setAwaitingAgent(true)
          jobId = response.data?.jobId ?? jobId
          await new Promise((resolve) => setTimeout(resolve, 1500))
          if (cancelled) return
          response = await client.getArchiveContents(archive.id, archive.name, currentPath, jobId)
        }
        if (cancelled) return
        setItems(response.data.items || [])
      } catch (err: unknown) {
        if (cancelled) return
        const error = err as { response?: { data?: { detail?: string } } }
        const errorMsg =
          translateBackendKey(error.response?.data?.detail) ||
          t('wizard.restoreFiles.failedToLoadContents')
        setError(errorMsg)
      } finally {
        if (!cancelled) {
          setLoading(false)
          setAwaitingAgent(false)
        }
      }
    }

    fetchContents()

    return () => {
      cancelled = true
    }
  }, [repository, archive.id, archive.name, currentPath, t])

  const pathParts = currentPath ? currentPath.split('/').filter(Boolean) : []

  const visibleItems = useMemo(() => {
    const needle = filter.trim().toLowerCase()
    if (!needle) return items
    return items.filter((item) => item.name.toLowerCase().includes(needle))
  }, [items, filter])

  const handleItemClick = (item: ArchiveItem) => {
    if (item.type === 'directory') {
      setCurrentPath(item.path)
    } else if (!isRestoreCanaryItem(item)) {
      toggleSelection(item)
    }
  }

  // The selected ancestor that already covers a path, if any. Borg extracts
  // whole trees and the restore has no exclude list, so a path under a
  // selected folder is included whatever happens to its own checkbox.
  const coveredBy = (path: string): string | null => {
    for (const selected of selectedPaths) {
      if (path.startsWith(selected + '/')) return selected
    }
    return null
  }

  const toggleSelection = (item: ArchiveItem) => {
    if (isRestoreCanaryItem(item) || coveredBy(item.path)) {
      return
    }

    const path = item.path
    const newPaths = new Set(selectedPaths)
    let newItems = selectedItems.filter((selectedItem) => selectedItem.path !== path)

    if (newPaths.has(path)) {
      newPaths.delete(path)
    } else {
      // Selecting a folder makes every selection inside it redundant, and
      // keeping them would double count the size and clutter the list.
      if (item.type === 'directory') {
        for (const selected of selectedPaths) {
          if (selected.startsWith(path + '/')) newPaths.delete(selected)
        }
        newItems = newItems.filter((selectedItem) => !selectedItem.path.startsWith(path + '/'))
      }
      newPaths.add(path)
      newItems = [...newItems, { path, type: item.type }]
    }

    onChange({ selectedPaths: Array.from(newPaths), selectedItems: newItems })
  }

  const navigateToPath = (targetPath: string) => {
    setFilter('')
    setCurrentPath(targetPath)
  }

  // The published callbacks are read through refs rather than captured, so a
  // wrapper that holds one from an earlier render still toggles against the
  // current selection instead of the one this effect last saw.
  const activateRef = useRef(handleItemClick)
  const navigateRef = useRef(navigateToPath)

  // Written in an effect rather than during render: a ref belongs to the
  // committed tree, and the published callbacks only read it when they run.
  useEffect(() => {
    activateRef.current = handleItemClick
    navigateRef.current = navigateToPath
  })

  useEffect(() => {
    onBrowseStateChange?.({
      currentPath,
      items: visibleItems,
      navigateTo: (path: string) => navigateRef.current(path),
      activateItem: (item: ArchiveItem) => activateRef.current(item),
      focusFilter: () => filterRef.current?.focus(),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPath, visibleItems])

  const formatSize = (bytes?: number): string => {
    if (!bytes) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`
  }

  const isSelected = (path: string): boolean => {
    return selectedPaths.has(path) || coveredBy(path) != null
  }

  const hasSelectedChildren = (dirPath: string): boolean => {
    return Array.from(selectedPaths).some((p) => p.startsWith(dirPath + '/'))
  }

  const getDirectoryIcon = (item: ArchiveItem) => {
    if (isRestoreCanaryItem(item)) {
      return <Square size={20} color="rgba(0, 0, 0, 0.35)" />
    }
    if (coveredBy(item.path)) {
      return <CheckSquare size={20} color="rgba(25, 118, 210, 0.5)" />
    }
    if (isSelected(item.path)) {
      return <CheckSquare size={20} color="#1976d2" />
    } else if (hasSelectedChildren(item.path)) {
      return <MinusSquare size={20} color="#1976d2" />
    } else {
      return <Square size={20} />
    }
  }
  const isInsideCanaryPath = isRestoreCanaryPath(currentPath)
  const canaryDescription = t('archiveContents.managedCanaryDescription')

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {!embedded && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            {title || t('wizard.restoreFiles.title')}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            {subtitle || t('wizard.restoreFiles.subtitle')}
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          minHeight: 32,
          display: 'flex',
          alignItems: 'center',
          mb: embedded ? 0 : 2,
          ...(embedded
            ? {
                px: 2,
                py: 1.25,
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: (theme: Theme) => alpha(theme.palette.text.primary, 0.025),
                borderTopLeftRadius: 'inherit',
                borderTopRightRadius: 'inherit',
              }
            : {}),
        }}
      >
        <Breadcrumbs
          separator={<ChevronRight size={16} />}
          sx={{
            flexWrap: 'nowrap',
            '& .MuiBreadcrumbs-ol': {
              display: 'flex',
            },
            '& .MuiBreadcrumbs-li': {
              display: 'flex',
            },
          }}
        >
          <Link
            component="button"
            variant="body2"
            onClick={() => navigateToPath('')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              cursor: 'pointer',
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            <Home size={16} />
            {t('wizard.restoreFiles.root')}
          </Link>
          {pathParts.map((part, index) => {
            const pathUpToHere = pathParts.slice(0, index + 1).join('/')
            const isLast = index === pathParts.length - 1
            return (
              <Link
                key={pathUpToHere}
                component="button"
                variant="body2"
                onClick={() => !isLast && navigateToPath(pathUpToHere)}
                sx={{
                  cursor: isLast ? 'default' : 'pointer',
                  textDecoration: 'none',
                  fontWeight: isLast ? 600 : 400,
                  '&:hover': { textDecoration: isLast ? 'none' : 'underline' },
                  whiteSpace: 'nowrap',
                }}
              >
                {part}
              </Link>
            )
          })}
        </Breadcrumbs>
        {embedded && (
          <TextField
            inputRef={filterRef}
            size="small"
            variant="outlined"
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
            placeholder={t('wizard.restoreFiles.filterPlaceholder')}
            sx={{ ml: 'auto', width: { xs: 140, sm: 220 }, flexShrink: 0 }}
            slotProps={{
              // The label belongs on the input, not on the wrapper MUI would
              // otherwise put it on.
              htmlInput: { 'aria-label': t('wizard.restoreFiles.filterLabel') },
              input: {
                startAdornment: (
                  <Search size={14} style={{ marginRight: 6, opacity: 0.6 }} aria-hidden />
                ),
              },
            }}
          />
        )}
      </Box>

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          // Embedded in the Files tab the panel around the list owns the
          // frame; a second border here read as a box inside a box.
          border: embedded ? 0 : '1px solid',
          borderColor: 'divider',
          borderRadius: embedded ? 0 : 1,
          bgcolor: 'background.paper',
          overflow: 'hidden',
        }}
      >
        {isInsideCanaryPath && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
              px: 1.5,
              py: 1,
              borderBottom: '1px solid',
              borderColor: (theme) => alpha(theme.palette.info.main, 0.22),
              bgcolor: (theme) => alpha(theme.palette.info.main, 0.07),
              flexShrink: 0,
            }}
          >
            <ShieldCheck size={16} style={{ marginTop: 2, flexShrink: 0 }} />
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
              }}
            >
              {t('archiveContents.managedCanaryProbeDisabled')}
            </Typography>
          </Box>
        )}

        {!embedded && (
          <Box
            sx={{
              px: 2,
              py: 1,
              borderBottom: '1px solid',
              borderColor: 'divider',
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05),
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              height: 40,
              minHeight: 40,
              maxHeight: 40,
            }}
          >
            <CheckSquare size={16} />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {selectedPaths.size > 0
                ? t('wizard.restoreFiles.itemsSelected', { count: selectedPaths.size })
                : t('wizard.restoreFiles.noItemsSelected')}
            </Typography>
            {selectedPaths.size > 0 && (
              <Chip
                label={t('wizard.restoreFiles.clearAll')}
                size="small"
                onClick={() => onChange({ selectedPaths: [], selectedItems: [] })}
                sx={{ ml: 'auto', cursor: 'pointer', height: 24 }}
              />
            )}
          </Box>
        )}

        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {loading && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
                p: 4,
              }}
            >
              <CircularProgress size={32} />
              {awaitingAgent && (
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    maxWidth: 420,
                    textAlign: 'center',
                  }}
                >
                  {t('archiveContents.slowLoadingHint')}
                </Typography>
              )}
            </Box>
          )}

          {error && (
            // Flush with the panel: the panel already owns the frame, so a
            // second rounded box inside it reads as a mistake.
            <Alert severity="error" sx={{ borderRadius: 0, px: 2.5, py: 1.5 }}>
              {error}
            </Alert>
          )}

          {!loading && !error && visibleItems.length === 0 && (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {t('wizard.restoreFiles.noItemsFound')}
              </Typography>
            </Box>
          )}

          {!loading && !error && visibleItems.length > 0 && (
            <List dense disablePadding>
              {visibleItems.map((item, index) => {
                const managedCanary = isRestoreCanaryItem(item)
                const isActive = activeIndex === index
                const managedTooltip =
                  item.type === 'directory'
                    ? canaryDescription
                    : t('archiveContents.managedCanaryProbeDisabled')
                return (
                  <Tooltip
                    key={item.path}
                    title={
                      managedCanary
                        ? managedTooltip
                        : coveredBy(item.path) && item.type !== 'directory'
                          ? t('archiveContents.includedByParent', { parent: coveredBy(item.path) })
                          : ''
                    }
                    arrow
                    disableHoverListener={!managedCanary && !coveredBy(item.path)}
                  >
                    <ListItem
                      disablePadding
                      secondaryAction={
                        item.type === 'directory' ? (
                          <Tooltip
                            title={
                              managedCanary
                                ? t('archiveContents.managedCanaryProbeDisabled')
                                : coveredBy(item.path)
                                  ? t('archiveContents.includedByParent', {
                                      parent: coveredBy(item.path),
                                    })
                                  : t('wizard.restoreFiles.selectDirTooltip')
                            }
                            describeChild
                          >
                            <span>
                              <IconButton
                                edge="end"
                                size="small"
                                aria-label={
                                  managedCanary
                                    ? t('archiveContents.managedCanaryProbeDisabled')
                                    : coveredBy(item.path)
                                      ? t('archiveContents.includedByParent', {
                                          parent: coveredBy(item.path),
                                        })
                                      : t('wizard.restoreFiles.selectDirTooltip')
                                }
                                disabled={managedCanary || coveredBy(item.path) != null}
                                onClick={() => toggleSelection(item)}
                              >
                                {getDirectoryIcon(item)}
                              </IconButton>
                            </span>
                          </Tooltip>
                        ) : null
                      }
                    >
                      <ListItemButton
                        onClick={() => handleItemClick(item)}
                        data-active={isActive || undefined}
                        ref={(node: HTMLDivElement | null) => {
                          if (isActive) node?.scrollIntoView({ block: 'nearest' })
                        }}
                        sx={{
                          py: embedded ? 1 : undefined,
                          px: embedded ? 2 : undefined,
                          ...(isActive && {
                            outline: (theme: Theme) =>
                              `2px solid ${alpha(theme.palette.primary.main, 0.5)}`,
                            outlineOffset: '-2px',
                          }),
                          // Embedded rows are separated by a hairline and never
                          // outlined; the panel around the list owns the frame.
                          ...(embedded
                            ? {
                                border: 0,
                                borderRadius: 0,
                                borderBottom: '1px solid',
                                borderBottomColor: 'divider',
                                '.MuiListItem-root:last-of-type &': { borderBottom: 0 },
                              }
                            : {
                                border: '1px solid',
                                borderColor: (theme: Theme) =>
                                  managedCanary
                                    ? alpha(theme.palette.info.main, 0.25)
                                    : 'transparent',
                              }),
                          // One resolver owns the background: a second
                          // bgcolor key later in the object would win and drop
                          // the keyboard cursor's tint.
                          bgcolor: (theme) =>
                            isActive
                              ? alpha(theme.palette.primary.main, 0.12)
                              : managedCanary
                                ? alpha(theme.palette.info.main, 0.05)
                                : 'transparent',
                          cursor:
                            item.type === 'directory'
                              ? 'pointer'
                              : managedCanary
                                ? 'default'
                                : 'pointer',
                          // The product's tables hover in a neutral tint; the
                          // blue tint is kept for the keyboard cursor only.
                          '&:hover': {
                            bgcolor: (theme) =>
                              managedCanary
                                ? alpha(theme.palette.info.main, 0.09)
                                : embedded
                                  ? alpha(theme.palette.text.primary, 0.03)
                                  : alpha(theme.palette.primary.main, 0.08),
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 44 }}>
                          {managedCanary ? (
                            <ShieldCheck size={20} />
                          ) : isSelected(item.path) && item.type !== 'directory' ? (
                            <Box
                              sx={{
                                width: 32,
                                height: 32,
                                borderRadius: '9px',
                                bgcolor: 'primary.main',
                                color: 'primary.contrastText',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <CheckSquare size={18} />
                            </Box>
                          ) : (
                            <FileTypeIcon name={item.name} type={item.type} />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box
                              sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}
                            >
                              <Typography
                                component="span"
                                variant="body2"
                                sx={{
                                  fontSize: '0.875rem',
                                  fontWeight: isSelected(item.path) ? 600 : 400,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {item.name}
                              </Typography>
                              {managedCanary && (
                                <Chip
                                  label={t('archiveContents.managedCanaryLabel')}
                                  size="small"
                                  color="info"
                                  variant="outlined"
                                  sx={{ height: 20, flexShrink: 0 }}
                                />
                              )}
                            </Box>
                          }
                          secondary={item.size ? formatSize(item.size) : undefined}
                          slotProps={{
                            secondary: { sx: { fontSize: '0.75rem' } },
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  </Tooltip>
                )
              })}
            </List>
          )}
        </Box>
      </Box>

      {!embedded && (
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            mt: 1,
          }}
        >
          {helpText || t('wizard.restoreFiles.helpText')}
        </Typography>
      )}
    </Box>
  )
}
